// File: src/composables/useAuthz.ts
/**
 * Authorization helpers for the frontend UI.
 *
 * @remarks
 * - Backend remains the source of truth; this composable only interprets claims.
 * - Secure defaults: if claims are missing/stale, checks return false.
 * - Keep UI logic declarative and DRY (no role strings sprinkled in components).
 *
 * @fileoverview Frontend authorization composable with reactive claims support
 * @version 1.0.0
 */

import { computed, type ComputedRef, type Ref } from 'vue'
import { ROLE_GROUPS, type RoleSlug, validateRoles, isValidRoleSlug } from '@/config/roles'

/**
 * Minimal shape of auth claims we consume on the frontend.
 * Extend fields freely as backend adds more, without changing the API here.
 */
export interface AuthzClaims {
    /** Granted role slugs (as issued by backend) */
    roles?: RoleSlug[]
    /** Expanded capability keys (from backend permission packs) */
    perms?: string[]
    /** Optional scope/context (tenant, domain-group, etc.) */
    scope?: {
        organizationId?: string | null
        domainGroupId?: string | null
        domainId?: string | null
    }
}

/**
 * Public API of the composable.
 */
export interface UseAuthz {
    /** Reactive list of role slugs (never null; falls back to []) */
    roles: ComputedRef<RoleSlug[]>
    /** Reactive list of capability keys (never null; falls back to []) */
    perms: ComputedRef<string[]>
    /** True when we have a claims object (arrays may still be empty) */
    isLoaded: ComputedRef<boolean>
    /** True when we can trust authorization checks (roles OR perms are present) */
    authzReady: ComputedRef<boolean>
    /** Explicit export for checking if roles are loaded */
    rolesReady: ComputedRef<boolean>
    /** Explicit export for checking if permissions are loaded */
    permsReady: ComputedRef<boolean>

    /** Does the user hold this exact role? */
    hasRole: (role: RoleSlug) => boolean
    /** Does the user hold at least one of these roles? */
    hasAnyRole: (...roles: RoleSlug[]) => boolean
    /** Is the user in a predefined role group? */
    inGroup: (group: keyof typeof ROLE_GROUPS) => boolean

    /** True if the user has this capability key (e.g., 'add_organization') */
    can: (perm: string) => boolean
    /** True if the user has any of the capability keys */
    canAny: (...perms: string[]) => boolean
    /** True if the user has all of the capability keys */
    canAll: (...perms: string[]) => boolean

    /** Convenience flags we commonly need in the UI */
    isPlatformStaff: ComputedRef<boolean>
    isTenantAdminish: ComputedRef<boolean>
    isReadonly: ComputedRef<boolean>

    /**
     * Prefer backend perms; until perms are present, fall back to role intent.
     * @param perm - Permission codename (e.g., 'change_organization')
     * @param roleFallback - Role-based intent (e.g., tenant MSP admin)
     * @returns true if permission granted or role fallback applies
     * @example canOrRole('change_organization', isTenantAdminish.value)
     */
    canOrRole: (perm: string, roleFallback: boolean) => boolean
}

/**
 * Create authorization helpers bound to a reactive claims source.
 *
 * @param claimsRef - A Ref of the current user's auth claims (roles, perms, scope).
 * @returns Object with authorization checking methods and reactive properties
 *
 * @example
 * ```typescript
 * // In a component
 * const session = useSessionStore()
 * const { can, hasAnyRole, isPlatformStaff } = useAuthz(session.claims)
 *
 * // Use in computed properties
 * const canCreateOrg = computed(() => can('org:write'))
 * const showAuditTab = computed(() => isPlatformStaff.value)
 *
 * // Use in methods
 * const handleEdit = () => {
 *   if (!can('org:edit')) {
 *     toast.error('Insufficient permissions')
 *     return
 *   }
 *   // ... proceed with edit
 * }
 * ```
 */
export function useAuthz(claimsRef: Ref<AuthzClaims | null | undefined>): UseAuthz {
    // sanitize & de-dupe
    const roles = computed<RoleSlug[]>(() => {
        const raw = claimsRef.value?.roles ?? []
        const valid = validateRoles(raw)
        return Array.from(new Set(valid))
    })
    const perms = computed<string[]>(() => {
        const raw = claimsRef.value?.perms ?? []
        const clean = raw.filter((p): p is string => typeof p === 'string')
        return Array.from(new Set(clean))
    })

    // set views for O(1) lookups
    const roleSet = computed(() => new Set<RoleSlug>(roles.value))
    const permSet = computed(() => new Set<string>(perms.value))

    const isLoaded = computed<boolean>(() => !!claimsRef.value)

    /**
     * Explicit readiness flags for roles and permissions.
     *
     * - rolesReady: True when we have at least one role
     * - permsReady: True when we have at least one permission
     * - authzReady: True when EITHER roles OR perms are present (allows role fallback)
     *
     * This allows canOrRole() to use role-based fallback while permissions are loading
     * or not yet configured on the backend.
     */
    const rolesReady = computed<boolean>(() => roles.value.length > 0)
    const permsReady = computed<boolean>(() => perms.value.length > 0)
    const authzReady = computed<boolean>(() => rolesReady.value || permsReady.value)

    const hasRole = (role: RoleSlug): boolean => roleSet.value.has(role)
    const hasAnyRole = (...needles: RoleSlug[]): boolean =>
        needles.some((r) => roleSet.value.has(r))
    const inGroup = (group: keyof typeof ROLE_GROUPS): boolean =>
        ROLE_GROUPS[group].some((r) => roleSet.value.has(r))

    const can = (perm: string): boolean => permSet.value.has(perm)
    const canAny = (...needles: string[]): boolean => needles.some((p) => permSet.value.has(p))
    const canAll = (...needles: string[]): boolean => needles.every((p) => permSet.value.has(p))

    const isPlatformStaff = computed(() => inGroup('PLATFORM_STAFF'))
    const isTenantAdminish = computed(() => inGroup('TENANT_ADMINISTRATORS'))
    const isReadonly = computed(() => inGroup('READONLY_ROLES'))

    /**
     * Gold-standard behavior:
     * - If perms are ready → trust backend perms (source of truth).
     * - If perms NOT ready → allow via role intent to avoid frontend-only denial.
     *
     * This prevents the "permissions loading window" problem where roles exist but
     * perms are still empty, causing false negatives in permission checks.
     *
     * @param perm - Permission codename to check (e.g., 'change_organization')
     * @param roleFallback - Boolean representing role-based access intent
     * @returns true if permission granted or role fallback applies during loading
     */
    const canOrRole = (perm: string, roleFallback: boolean): boolean =>
        permsReady.value ? can(perm) : roleFallback

    return {
        roles,
        perms,
        isLoaded,
        authzReady,
        rolesReady,
        permsReady,
        hasRole,
        hasAnyRole,
        inGroup,
        can,
        canAny,
        canAll,
        isPlatformStaff,
        isTenantAdminish,
        isReadonly,
        canOrRole
    }
}

/**
 * Helper to create a mock claims object for testing.
 * Useful in unit tests and Storybook stories.
 *
 * @param overrides - Partial claims to override defaults
 * @returns Complete AuthzClaims object suitable for testing
 *
 * @example
 * ```typescript
 * // In tests
 * const adminClaims = createMockClaims({
 *   roles: [ROLES.PLATFORM.ADMIN],
 *   perms: ['org:write', 'org:delete', 'user:admin']
 * })
 *
 * const { can } = useAuthz(ref(adminClaims))
 * expect(can('org:write')).toBe(true)
 * ```
 */
export function createMockClaims(overrides: Partial<AuthzClaims> = {}): AuthzClaims {
    return {
        roles: [],
        perms: [],
        scope: {
            organizationId: null,
            domainGroupId: null,
            domainId: null
        },
        ...overrides
    }
}

/**
 * Type guard to validate AuthzClaims structure.
 * Useful for runtime validation of claims from external sources.
 *
 * @param value - The value to validate
 * @returns True if the value matches AuthzClaims interface
 */
export function isValidAuthzClaims(value: unknown): value is AuthzClaims {
    if (!value || typeof value !== 'object') return false
    const claims = value as Record<string, unknown>

    if (claims.roles !== undefined) {
        if (!Array.isArray(claims.roles)) return false
        if (!claims.roles.every(isValidRoleSlug)) return false
    }
    if (claims.perms !== undefined) {
        if (!Array.isArray(claims.perms)) return false
        if (!claims.perms.every((perm) => typeof perm === 'string')) return false
    }
    if (claims.scope !== undefined && (claims.scope === null || typeof claims.scope !== 'object')) {
        return false
    }
    return true
}
